/** The pure parts of the kit's SortableList (the form embed round, 2026-10-26: the drag on dnd-kit, the founder's look kept): where a carried
 *  row lands, where the insertion line goes, and one arrow key's step. No DOM; `components/SortableList.tsx` feeds them the rows' places on
 *  screen, measured anew on every move, so a list that scrolled during the drag still drops right. */

type Box = { top: number; bottom: number };
type Row = Box & { left: number; width: number };

/** where a row carried by the pointer lands: the row under the pointer (or the next one below it, the last one past the end) takes the drop
 *  before it in its upper half and after it in its lower half, so the first and the last place are both reachable and a row dropped past its
 *  list's end moves to the last place. `from` is the carried row; answers its new index as `reorder` takes it (`from` itself: no move). */
export function landingIndex(rows: readonly Box[], y: number, from: number): number {
  if (!rows.length) return from;
  let i = rows.findIndex((r) => y < r.bottom);
  const past = i < 0;
  if (past) i = rows.length - 1;
  const r = rows[i]!;
  const before = !past && y < (r.top + r.bottom) / 2;
  const pos = before ? i : i + 1;
  return pos > from ? pos - 1 : pos;
}

/** the insertion line for a row carried from `from` to `at`: a 2 px line on the target row's top edge when moving up, on its bottom edge when
 *  moving down, none while the row would stay where it is */
export function insertionLine(rows: readonly Row[], from: number, at: number): { top: number; left: number; width: number } | null {
  const r = rows[at];
  if (!r || at === from) return null;
  return { top: (at < from ? r.top : r.bottom) - 1, left: r.left, width: r.width };
}

/** one arrow key during a keyboard drag of a list of `count` rows: the next place, kept inside the list; null for any other key */
export function keyStep(at: number, code: string, count: number): number | null {
  if (code === "ArrowUp") return Math.max(0, at - 1);
  if (code === "ArrowDown") return Math.min(count - 1, at + 1);
  return null;
}

/* ---------- the nested mode (the sidebar editor rework, 2026-10-26): entries that move within and across groups ---------- */

/** a group as measured on screen: the whole section, its heading row, its entry rows */
export type NestedGroupBox = { box: Box; head: Box; rows: readonly Box[] };
/** a place in the nested list: the group's index and the entry's index in it (as the move takes it) */
export type NestedPlace = { group: number; index: number };

/** where an entry carried from `from` lands in another group when the pointer is at `y` inside it: before a row in its upper half, after it in
 *  its lower half; the heading, the rest of the section under the rows (its "+ Add" line) and a place past the rows: the group's end */
function landingInOther(g: NestedGroupBox, y: number): number {
  if (y < g.head.bottom) return g.rows.length;
  const i = g.rows.findIndex((r) => y < r.bottom);
  if (i < 0) return g.rows.length;
  const r = g.rows[i]!;
  return y < (r.top + r.bottom) / 2 ? i : i + 1;
}
/** a group's last place for the carried entry: one less in its own group (it leaves its place there) */
const endOf = (groups: readonly NestedGroupBox[], g: number, from: NestedPlace) => Math.max(0, groups[g]!.rows.length - (g === from.group ? 1 : 0));

/** where an entry carried by the pointer lands among groups (the Customize dialog's sections, measured right now): in its own group the flat
 *  rule (`landingIndex`; its own heading means its first place); in another group `landingInOther`; an empty group takes it as its first;
 *  between two groups it goes to the end of the group above; above every group to the very first place; below every group to the last
 *  group's end. Answers `from` itself when nothing would move. */
export function nestedLanding(groups: readonly NestedGroupBox[], y: number, from: NestedPlace): NestedPlace {
  if (!groups.length) return from;
  const g = groups.findIndex((x) => y < x.box.bottom);
  if (g < 0) return { group: groups.length - 1, index: endOf(groups, groups.length - 1, from) };
  const box = groups[g]!;
  if (y < box.box.top) return g === 0 ? { group: 0, index: 0 } : { group: g - 1, index: endOf(groups, g - 1, from) };
  if (g !== from.group) return { group: g, index: landingInOther(box, y) };
  if (y < box.head.bottom) return { group: g, index: 0 };
  return { group: g, index: landingIndex(box.rows, y, from.index) };
}

/** one arrow key during a keyboard drag of an entry among groups whose entry counts are `sizes` (the carried entry still counted in its own
 *  group, so a newcomer has one more place in another group): ArrowDown past a group's last place goes to the next group's first place, ArrowUp
 *  before a group's first place to the last place of the group above, empty groups being stops too; the ends of the whole list hold; null for
 *  any other key */
export function nestedKeyStep(at: NestedPlace, code: string, sizes: readonly number[], from: NestedPlace): NestedPlace | null {
  const last = (g: number) => Math.max(0, sizes[g]! - (g === from.group ? 1 : 0));
  if (code === "ArrowDown") {
    if (at.index < last(at.group)) return { group: at.group, index: at.index + 1 };
    return at.group + 1 < sizes.length ? { group: at.group + 1, index: 0 } : at;
  }
  if (code === "ArrowUp") {
    if (at.index > 0) return { group: at.group, index: at.index - 1 };
    return at.group > 0 ? { group: at.group - 1, index: last(at.group - 1) } : at;
  }
  return null;
}
